import { useEffect, useState } from "react";

const COINS = ["BTC", "ETH", "SOL"];
const RULES = new Set(["Price ceiling", "Min contracts"]);

const passes = (r, ceil, min, spread) =>
  r.ask_cents != null && r.ask_cents <= ceil && (r.depth ?? 0) >= min && r.bid_cents != null && r.ask_cents - r.bid_cents <= spread;

function Field({ testid, value, onChange, step = "1" }) {
  return (
    <input data-testid={testid} type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-14 bg-transparent border border-[var(--border-active)] rounded px-1.5 py-0.5 font-mono-num text-xs focus:outline-none focus:border-cyan-400" />
  );
}

const defaults = (c) => Object.fromEntries(COINS.map((a) => [a, { thr: c.velocity_threshold[a], ceil: c.max_entry_cents[a], min: c.min_contract_depth[a] }]));

export default function WhatIf({ rows, nearMisses, config }) {
  const key = JSON.stringify([config.velocity_threshold, config.max_entry_cents, config.min_contract_depth]);
  const [t, setT] = useState(() => defaults(config));
  useEffect(() => setT(defaults(config)), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (a, k, v) => setT((p) => ({ ...p, [a]: { ...p[a], [k]: v } }));
  const ok = (r, spread = config.max_spread_cents) => passes(r, Number(t[r.asset].ceil), Number(t[r.asset].min), spread);
  const skips = rows.filter((r) => r.outcome === "skipped" && RULES.has(r.rule));
  const fired = nearMisses.filter((r) => Number(t[r.asset].thr) > 0 && Math.abs(r.delta) >= Number(t[r.asset].thr));
  const won = (a) => skips.filter((r) => r.asset === a && ok(r)).length;
  const nmFire = (a) => fired.filter((r) => r.asset === a);
  const total = COINS.reduce((n, a) => n + won(a), 0);
  const nmTrade = fired.filter((r) => ok(r)).length;
  const bySpread = [...skips, ...fired].filter((r) => ok(r, 99) && !ok(r)).length;
  return (
    <div data-testid="what-if" className="rounded-lg border border-[var(--border-subtle)] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-mute)] mb-2">What-if · today's skips &amp; near-misses</div>
      <table className="w-full text-xs font-mono-num">
        <thead className="text-[10px] text-[var(--text-mute)] text-left">
          <tr><th>Coin</th><th>Move $</th><th>Max ¢</th><th>Min ct</th><th className="text-right">Skips → trade</th><th className="text-right">Near-miss fire · trade</th></tr>
        </thead>
        <tbody>{COINS.map((a) => {
          const k = a.toLowerCase();
          return (
            <tr key={a} className="border-t border-[var(--border-subtle)]">
              <td className="py-1">{a}</td>
              <td><Field testid={`whatif-${k}-threshold`} step="any" value={t[a].thr} onChange={(v) => set(a, "thr", v)} /></td>
              <td><Field testid={`whatif-${k}-ceiling`} value={t[a].ceil} onChange={(v) => set(a, "ceil", v)} /></td>
              <td><Field testid={`whatif-${k}-min`} value={t[a].min} onChange={(v) => set(a, "min", v)} /></td>
              <td data-testid={`whatif-${k}-result`} className="text-right">{won(a)} / {skips.filter((r) => r.asset === a).length}</td>
              <td data-testid={`whatif-${k}-nearmiss`} className="text-right">{nmFire(a).length} · {nmFire(a).filter((r) => ok(r)).length}</td>
            </tr>);
        })}</tbody>
      </table>
      <div data-testid="whatif-total" className="mt-2 text-xs text-[var(--text-dim)]">
        {total} of {skips.length} skips would have traded · view only, settings are unchanged
      </div>
      <div data-testid="whatif-nearmiss-total" className="mt-1 text-xs text-[var(--text-dim)]">
        {fired.length} of {nearMisses.length} near-misses would fire at these moves · {nmTrade} would trade
      </div>
      {bySpread > 0 && (
        <div data-testid="whatif-spread-blocked" className="mt-1 text-xs text-amber-300">
          {bySpread} more would pass these values but are blocked by the {config.max_spread_cents}¢ spread limit
        </div>
      )}
      <div className="mt-1 text-[10px] text-[var(--text-mute)]">Near-misses only cover moves of 80%+ of your saved trigger. Upper bound: budget, open-trade and loss-cap limits aren't re-checked</div>
    </div>
  );
}
